import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { Link, useNavigate } from 'react-router-dom';
import { BiCrown } from 'react-icons/bi';
import UserSubscription from '../components/UserSubscription';
import SubscriptionHistory from '../components/SubscriptionHistory';
import LoadingSpinner from '../components/LoadingSpinner';
import { serverTimestamp } from 'firebase/firestore';

const MyAccount = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [subscription, setSubscription] = useState(null);

  // Function to check if a subscription has expired
  const hasSubscriptionExpired = (expiryDate) => {
    if (!expiryDate) return true;
    
    let expiryDateObj;
    
    // Handle different date formats
    if (expiryDate.seconds) {
      expiryDateObj = new Date(expiryDate.seconds * 1000);
    } else if (expiryDate instanceof Date) {
      expiryDateObj = expiryDate;
    } else if (typeof expiryDate === 'string') {
      expiryDateObj = new Date(expiryDate);
    } else if (typeof expiryDate === 'number') {
      expiryDateObj = new Date(expiryDate);
    } else {
      return true; // If format is unknown, assume expired
    }
    
    const now = new Date();
    return expiryDateObj < now;
  };

  // Function to update subscription status in database
  const updateSubscriptionStatus = async (userId, docId, isExpired) => {
    try {
      // First try updating in purchases collection
      const purchaseRef = doc(db, 'purchases', docId);
      await updateDoc(purchaseRef, {
        status: isExpired ? 'inactive' : 'active',
        updatedAt: serverTimestamp()
      });
      console.log('Successfully updated subscription status in purchases collection');
      return true;
    } catch (purchaseError) {
      console.error('Error updating purchases document:', purchaseError);
      
      // If that fails, try the users collection
      try {
        const userDocRef = doc(db, 'users', userId);
        await updateDoc(userDocRef, {
          subscriptionStatus: isExpired ? 'inactive' : 'active',
          updatedAt: serverTimestamp()
        });
        console.log('Successfully updated subscription status in users collection');
        return true;
      } catch (userError) {
        console.error('Error updating user document:', userError);
        
        // If using email as document ID in users collection
        try {
          if (user && user.email) {
            const userByEmailRef = doc(db, 'users', user.email);
            await updateDoc(userByEmailRef, {
              subscriptionStatus: isExpired ? 'inactive' : 'active',
              updatedAt: serverTimestamp()
            });
            console.log('Successfully updated subscription status in users collection by email');
            return true;
          }
        } catch (emailError) {
          console.error('Error updating user document by email:', emailError);
          return false;
        }
      }
    }
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.name) {
            const nameParts = userData.name.split(' ');
            setFirstName(nameParts[0] || '');
            setLastName(nameParts.slice(1).join(' ') || '');
          }
        }

        // Set email from auth user object
        setEmail(user.email || '');
        
        // Fetch subscription data and check if expired
        await fetchSubscriptionAndCheckExpiry();
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setLoading(false);
      }
    };

    const fetchSubscriptionAndCheckExpiry = async () => {
      if (!user) return;
      
      const userId = user.uid;
      let subscriptionData = null;
      let docId = null;
      
      try {
        // First try: Check purchases collection using userId
        const purchaseRef = doc(db, 'purchases', userId);
        const purchaseDoc = await getDoc(purchaseRef);

        if (purchaseDoc.exists()) {
          subscriptionData = purchaseDoc.data();
          docId = userId;
        } else {
          // Second try: Check users collection using userId
          const userDocRef = doc(db, 'users', userId);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists() && (userDoc.data().subscriptionStatus || userDoc.data().planType)) {
            subscriptionData = {
              userId: userId,
              email: user.email,
              planType: userDoc.data().planType || 'free',
              status: userDoc.data().subscriptionStatus || 'unknown',
              expiryDate: userDoc.data().expiryDate,
              createdAt: userDoc.data().createdAt,
              customerId: userDoc.data().customerId,
              amount: userDoc.data().amount || 0,
              currency: userDoc.data().currency || 'usd'
            };
            docId = userId;
          } else {
            // Third try: Check users collection using email
            if (user.email) {
              const userByEmailRef = doc(db, 'users', user.email);
              const userByEmailDoc = await getDoc(userByEmailRef);
              
              if (userByEmailDoc.exists() && (userByEmailDoc.data().subscriptionStatus || userByEmailDoc.data().planType)) {
                subscriptionData = {
                  userId: userId,
                  email: user.email,
                  planType: userByEmailDoc.data().planType || 'free',
                  status: userByEmailDoc.data().subscriptionStatus || 'unknown',
                  expiryDate: userByEmailDoc.data().expiryDate,
                  createdAt: userByEmailDoc.data().createdAt,
                  customerId: userByEmailDoc.data().customerId,
                  amount: userByEmailDoc.data().amount || 0,
                  currency: userByEmailDoc.data().currency || 'usd'
                };
                docId = user.email;
              }
            }
          }
        }
        
        if (subscriptionData) {
          setSubscription(subscriptionData);
          
          // Check if the subscription has expired but still marked as active
          const isExpired = hasSubscriptionExpired(subscriptionData.expiryDate);
          if (isExpired && subscriptionData.status === 'active') {
            console.log('Subscription has expired but is still marked as active. Updating status...');
            await updateSubscriptionStatus(userId, docId, true);
            
            // Update local state to reflect the change
            setSubscription({
              ...subscriptionData,
              status: 'inactive'
            });
          }
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
      }
    };

    fetchUserProfile();
  }, [user]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!user) return;

    try {
      setIsUpdating(true);
      
      // Update user document in Firestore
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        name: `${firstName} ${lastName}`,
        updatedAt: new Date()
      });
      
      setUpdateSuccess(true);
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setUpdateSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-3xl font-bold mb-2">Account Settings</h1>
      <p className="text-gray-600 mb-8">Update your profile, check your plan, or update your plan.</p>

      {/* Profile Information */}
      <div className="bg-white rounded-lg border mb-8 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Profile</h2>
        </div>
        <div className="p-6">
          <form onSubmit={handleUpdateProfile}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="mb-6">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              />
            </div>
            <div>
              <button
                type="submit"
                disabled={isUpdating}
                className="btn-theme text-white font-medium py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {isUpdating ? 'Updating...' : 'Update'}
              </button>
              {updateSuccess && (
                <span className="ml-4 text-green-600">Profile updated successfully!</span>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Current Plan */}
      <div className="bg-white rounded-lg border mb-8 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Current Plan</h2>
        </div>
        <div className="p-6">
          <UserSubscription />
          
          {/* <div className="mt-6">
            <Link 
              to="/plans"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white btn-theme hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <BiCrown className="mr-2 h-5 w-5" />
              Upgrade Plan
            </Link>
          </div> */}
        </div>
      </div>

      {/* Purchase History */}
      {/* <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Subscription History</h2>
        </div>
        <div className="p-6">
          <SubscriptionHistory />
        </div>
      </div> */}
    </div>
  );
};

export default MyAccount;